import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Form, Input, Button, Typography, Alert } from 'antd';
import PropTypes from 'prop-types';
import '../common.css';

const { Title, Text } = Typography;

function SignIn({ setUserData }) {
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);

  const { signIn } = useAuth(); 
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  const handleSignIn = async (values) => {
    // Function logic remains the same
    setEmailError('');
    setPasswordError('');
    setLoading(true);

    try {
      const { email, password } = values;
      const deviceName = navigator.userAgent;

      const response = await fetch(`${API_URL}/auth/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email, 
          password, 
          deviceName 
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        const { accessToken, refreshToken, userId, role, isTempPassword } = responseData;

        localStorage.setItem('token', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('userId', userId);
        localStorage.setItem('role', role);
        signIn(accessToken);
        setUserData({ userId, role });

        if (isTempPassword) {
          navigate('/change-password');
        } else {
          navigate('/dashboard');
        }
      } else {
        const errorResponse = await response.json();
        switch (errorResponse.error) {
          case 'UserNotFound':
            setEmailError('The email address is not registered.');
            break;
          case 'InvalidPassword':
            setPasswordError('The password is incorrect.');
            break;
          default:
            setEmailError('Email or password is incorrect.');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setEmailError('Email or password is incorrect.');
    } finally {
      setLoading(false);
    }
  };

 return (
  <div style={{
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #29166f, #29166f)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px',
  }}>
    <div style={{
      width: '100%',
      maxWidth: '460px',
      background: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(6px)',
      borderRadius: '20px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
      padding: '56px 44px',
      border: '1px solid rgba(255, 255, 255, 0.3)',
    }}>

      {/* Logo & Header */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <div style={{
          width: '96px',
          height: '96px',
          margin: '0 auto 20px',
          background: '#fff',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 18px rgba(0,0,0,0.1)'
        }}>
          <img 
            src="/images/tfulogo.png" 
            alt="TFU Logo" 
            style={{ width: '60%', objectFit: 'contain' }}
          />
        </div>
        <Title level={3} style={{ color: '#1e1b4b', marginBottom: 4, fontWeight: 700 }}>
          Welcome Back
        </Title>
        <Text style={{ color: '#6b7280', fontSize: '15px' }}>
          Sign in to continue
        </Text>
      </div>

      {/* Sign In Form */}
      <Form layout="vertical" onFinish={handleSignIn} requiredMark={false}>
        <Form.Item
          name="email"
          label={<span style={{ fontWeight: '600', fontSize: '14px' }}>Email Address</span>}
          rules={[
            { required: true, message: 'Please input your email!' },
            { type: 'email', message: 'Enter a valid email address.' }
          ]}
        >
          <Input
            size="large"
            placeholder="you@example.com"
            style={{
              borderRadius: '10px',
              borderColor: '#d1d5db',
              background: '#f9fafb'
            }}
          />
        </Form.Item>

        <Form.Item
          name="password"
          label={<span style={{ fontWeight: '600', fontSize: '14px' }}>Password</span>}
          rules={[{ required: true, message: 'Please input your password!' }]}
        >
          <Input.Password
            size="large"
            placeholder="••••••••"
            style={{
              borderRadius: '10px',
              borderColor: '#d1d5db',
              background: '#f9fafb'
            }}
          />
        </Form.Item>

        {emailError && (
          <Alert
            message={emailError}
            type="error"
            showIcon
            style={{
              marginBottom: '16px',
              borderRadius: '8px',
              background: '#fef2f2',
              borderColor: '#fecaca'
            }}
          />
        )}
        {passwordError && (
          <Alert
            message={passwordError}
            type="error"
            showIcon
            style={{
              marginBottom: '16px',
              borderRadius: '8px',
              background: '#fef2f2',
              borderColor: '#fecaca'
            }}
          />
        )}

        <div style={{
          textAlign: 'center',
          fontSize: '13px',
          color: '#6b7280',
          background: '#f3f4f6',
          padding: '12px 16px',
          borderRadius: '10px',
          border: '1px solid #e5e7eb',
          marginBottom: '28px',
        }}>
          Forgot your credentials? Contact your school administrator for support.
        </div>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            block
            style={{
              height: '48px',
              borderRadius: '10px',
              fontWeight: '600',
              fontSize: '16px',
              backgroundColor: '#1e1b4b',
              borderColor: '#1e1b4b',
              boxShadow: '0 4px 14px rgba(30, 27, 75, 0.3)',
              transition: 'all 0.3s ease'
            }}
          >
            Sign In
          </Button>
        </Form.Item>
      </Form>
    </div>
  </div>
);

}

SignIn.propTypes = {
  setUserData: PropTypes.func.isRequired,
};

export default SignIn;